/*
 * Copyright (c) 2020 Honeywell International, Inc. All rights reserved.
 *
 * This file contains trade secrets of Honeywell International, Inc.  No part
 *  may be reproduced or transmitted in any form by any means or for any
 *  purpose without the express written permission of Honeywell.
 *
 */

import React from 'react';
import {Icon} from "@scuf/common";
import './DIYStatusPage.scss'
import DIYAppConstants from "../../utils/DIYAppConstants";

/**
 * The list of possible status
 */
export enum DIYOperationStatus {
  SUCCESS, WARNING, ERROR, IMPORTANT, UNKNOWN
}

/**
 * The interface for the StatusIcon component
 */
export interface IDIYStatusIconProps {
  /**
   * The status of the Operation (DIY APIs or Validation)
   */
  status: DIYOperationStatus;
}

/**
 * The interface for the status screen properties
 */
export interface IDIYStatusProps extends IDIYStatusIconProps {
  /**
   * The title for the error screen
   */
  title: string;

  /**
   * An optional status message to be displayed with
   * the title and icon
   */
  message?: string
}

/**
 * This represents the generic error screen for the DIY application.
 * It displays the following content
 * 1. title
 * 2. a status icon (ERROR/WARNING/IMPORTANT/SUCCESS)
 * 3. the status content passed as component
 * 4. an optional message to be displayed below the status icon
 *
 * The following sections represent some of the ways this component can be used
 *  With inline JSX.Element
 *  @example
 *  <withStatus(<SomeClassComponent>) title= 'xxx' status = {DIYOperationStatus.SUCCESS}>
 *
 *  @example
 *  let Wrapper = withStatus(<SomeReactFC title= 'xxx' message='yyy' status = {DIYOperationStatus.ERROR}>)
 *  <Wrapper {...props}>
 *
 *  @example
 *  @withStatus(title)
 */
  // @Todo. Right now many screens in the aplication are PureComponents and hence the argument type is not just confined to FunctionCmponent. Subsequent refactors should aim to migrate completely to Function components
export const withStatus = <P extends object>(WrappedComponent: React.ComponentType<P>):
    React.FC<P & IDIYStatusProps> =>
    ({
       title,
       status,
       ...props
     }: IDIYStatusProps) =>

      <div className='ui-diy-status-root'>
        <div>
          <div className='ui-status-page-title'>
            {title}
          </div>
          <div className='ui-diy-status-icon'>
            <StatusIcon status={status}/>
          </div>
          {
            (!!props.message) ?
              <div className='ui-diy-status-msg'>
                {
                  props.message.split('\n').map((item, i) => {
                    return <div key={i}>{item}</div>;
                  })
                }
              </div> : <></>
          }
        </div>
        <WrappedComponent {...props as P}></WrappedComponent>
      </div>
;

/**
 * returns an icon corresponding to the status.
 * @param {IDIYStatusIconProps} props
 * @constructor
 */
export const StatusIcon = (props: IDIYStatusIconProps) => {
  let status = props.status;
  let iconProps: any = {
    root: "common",
    size: "xlarge",
    fitted: true,
    exactSize: 128
  };
  let ret;
  switch (status) {
    case DIYOperationStatus.SUCCESS:
      ret = <Icon name="badge-check"
                  color={`${DIYAppConstants.SUCCESS_COLOR}`} {...iconProps} />;
      break;
    case DIYOperationStatus.IMPORTANT:
      ret = <Icon name="badge-important"
                  color={`${DIYAppConstants.IMPORTANT_COLOR}`} {...iconProps} />;
      break;
    case DIYOperationStatus.ERROR:
      ret = <Icon name="badge-stop"
                  color={`${DIYAppConstants.ERROR_COLOR}`} {...iconProps} />;
      break;
    case DIYOperationStatus.WARNING:
      ret = <Icon name="badge-warning"
                  color={`${DIYAppConstants.WARNING_COLOR}`} {...iconProps} />;
      break;
    default:
      ret = <Icon name="badge-help"
                  color={`${DIYAppConstants.DEFAULT_COLOR}`} {...iconProps} />;
  }
  return ret;
};
